// src/components/UI/AddCard.tsx
import React from 'react';
import Icon, { IconName as BaseIconName } from '../UI/Icon/Icon';

// Extend IconName to include any custom icons you want to allow
export type IconName = BaseIconName | 'dog' | 'cat' | 'bird';

/**
 * Props for the AddCard component.
 *
 * @property {IconName} [icon] - Optional icon to display in the card.
 * @property {{ mobile: number; desktop: number }} [size] - Optional size configuration for mobile and desktop views.
 * @property {string} label - Label text to display on the card.
 * @property {() => void} [onClick] - Optional click handler for the card.
 * @property {string} [className] - Optional additional CSS classes for custom styling.
 */
type AddCardProps = {
    icon?: IconName;
    size?: { mobile: number; desktop: number };
    label: string;
    onClick?: () => void;
    className?: string; // optional additional classes
};

/**
 * Renders a card component displaying an animal icon and label.
 * 
 * The icon size adapts responsively based on the window width (mobile or desktop).
 * Handles click events and allows custom styling via `className`.
 *
 * @param icon - The name of the icon to display.
 * @param size - Object specifying icon sizes for mobile and desktop. Defaults to `{ mobile: 30, desktop: 60 }`.
 * @param label - The label text to display below or beside the icon.
 * @param onClick - Callback function invoked when the card is clicked.
 * @param className - Additional CSS classes for custom styling. Defaults to an empty string.
 *
 * @returns A responsive animal card component.
 */
const AnimalCard: React.FC<AddCardProps> = ({
    icon,
    size = { mobile: 30, desktop: 60 },
    label,
    onClick,
    className = '',
}) => {
    const [windowWidth, setWindowWidth] = React.useState(window.innerWidth);

    React.useEffect(() => {
        const handleResize = () => setWindowWidth(window.innerWidth);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const iconSize = windowWidth < 768 ? size.mobile : size.desktop;

    return (
        <div className="col-6 col-md">
            <div
                className={`bg-light-blue hover:bg-medium-blue radius-5px p-10px h-60px h-md-150px d-flex flex-row flex-md-column gap-10px gap-md-13px align-items-center justify-content-md-center text-md-center cursor-pointer ${className}`}
                onClick={onClick}
            >
                {icon && (
                    <Icon name={icon} size={iconSize} className="min-w-30px" />
                )}
                <div className="fs-14px fs-md-16px fw-bold">{label}</div>
            </div>
        </div>
    );
};

export default AnimalCard;
